<script setup>
import { computed } from "vue";
import { trackRudderstackImageEdited } from "common/utils/rudderstack/tracker";
import debounce from "lodash.debounce";
import RedrawNotes from "./RedrawNotes.vue";
import { useDtfStore } from "./store";
import TrimInfo from "./TrimInfo.vue";
import UseSvg from "../vue-cart/UseSvg.vue";

const props = defineProps({
  artwork: {
    type: Object,
    default: null
  },
  index: {
    type: Number,
    default: 0
  },
  activeConfigSection: {
    type: String, // jiffy-print-perfect | print-as-uploaded
    default: null
  }
});

const emit = defineEmits(["set-active-config-section"]);

const store = useDtfStore();

const isStandardArtwork = computed(() => store.isStandardArtwork);
const isPrintAsUploadedDisabled = computed(
  () =>
    isStandardArtwork.value && (props.artwork?.isLoading || !props.artwork?.enhancementCompleted)
);
const showRedrawNote = computed(() => store.showRedrawUI);

const debouncedToggleEnhancement = debounce(async (artworkId, enableEnhancement) => {
  await store.updateEnableEnhancement({ artworkId, enableEnhancement });
}, 100);

function setActiveConfigSection(section) {
  if (section === "print-as-uploaded" && isPrintAsUploadedDisabled.value) {
    return;
  }

  emit("set-active-config-section", section);

  if (props.artwork?.id) {
    const enableEnhancement = section === "jiffy-print-perfect";
    debouncedToggleEnhancement(props.artwork.id, enableEnhancement);
  }

  const trackPayloadData = {};
  if (section === "print-as-uploaded") {
    trackPayloadData.enhancementTier = null;
    trackPayloadData.editType = "Print as uploaded";
  } else if (section === "jiffy-print-perfect") {
    trackPayloadData.enhancementTier = "PrintPerfect";
    trackPayloadData.editType = "Jiffy Print Perfect";
  }

  trackRudderstackImageEdited({
    // eslint-disable-next-line no-nested-ternary
    product_line: store.sticker ? "sticker" : store.isProColor ? "pro color" : "dtf",
    artwork: {
      ...props.artwork,
      enhancementTier: trackPayloadData.enhancementTier
    },
    edit_type: trackPayloadData.editType,
    artworks_count: store.artworks.length,
    location: "pdp",
    pre_cut_transfers: store.preCut
  });
}
</script>

<template>
  <div class="artwork-quality-config-panel" :class="$attrs.class">
    <div
      class="artwork-quality-config-panel__section"
      :class="{
        'artwork-quality-config-panel__section--active':
          !artwork?.id || activeConfigSection === 'jiffy-print-perfect'
      }"
    >
      <button
        class="artwork-quality-config-panel__caption"
        @click.stop.prevent="setActiveConfigSection('jiffy-print-perfect')"
      >
        <UseSvg icon="checkmark-circled" class="artwork-quality-config-panel__caption-icon" />
        <span>Jiffy Print Perfect</span>
      </button>
      <div class="artwork-quality-config-panel__body">
        <TrimInfo
          :id="artwork.id"
          :artwork="artwork"
          :background="artwork.background"
          :can-remove-bg="artwork.canRemoveBg"
          :remove-background="artwork.removeBackground"
          :toggle-background-error="artwork.toggleBackgroundError"
          :active-config-section="activeConfigSection"
        />
        <RedrawNotes
          v-if="showRedrawNote"
          :enhancement-note="artwork.enhancementNote"
          :index="index"
        />
      </div>
    </div>
    <div
      class="artwork-quality-config-panel__section"
      :class="{
        'artwork-quality-config-panel__section--active':
          artwork?.id && activeConfigSection === 'print-as-uploaded',
        'artwork-quality-config-panel__section--disabled': isPrintAsUploadedDisabled
      }"
    >
      <button
        class="artwork-quality-config-panel__caption"
        @click.stop.prevent="setActiveConfigSection('print-as-uploaded')"
      >
        <UseSvg icon="checkmark-circled" class="artwork-quality-config-panel__caption-icon" />
        <span>Print as uploaded</span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings";
@import "frontend/init/jiffy/mixin";

.artwork-quality-config-panel {
  $this: &;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid $gray-border;
  background: $white;
  box-shadow: 0px 6px 10px 0px rgba($black, 0.05);

  &__section {
    &--disabled {
      opacity: 0.5;
    }

    &--active {
      #{$this}__caption {
        color: $white;
        background: $primary-light;
        pointer-events: none;
      }

      #{$this}__caption-icon {
        border-color: $white;
        color: $primary-light;
      }

      #{$this}__body {
        display: flex;
      }

      &:has(#{$this}__body) {
        #{$this}__caption {
          border-radius: 8px 8px 0px 0px;
        }
      }
    }
  }

  &__caption {
    display: flex;
    align-items: center;
    gap: 8px;
    background: $light-gray-blueish;
    border-radius: 8px;
    padding: 10px 16px;
    color: $black-light;
    font-size: 14px;
    font-weight: 700;
    cursor: default;
    width: 100%;
    border: none;
  }

  &__caption-icon {
    fill: $white;
    width: 24px;
    height: 24px;
    color: $white;
    background-color: $white;
    border: 1px solid $mischka;
    border-radius: 50%;
  }

  &__body {
    border: 1px solid $primary-light;
    border-radius: 0px 0px 8px 8px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    display: none;
  }
}
</style>
